/**
 * RP-7.6 (2026-10-17) — the warning shown when changing a line's Resource takes the plan
 * below its margin floor.
 *
 * Naming or confirming a Resource can move margin (their Internal Rate replaces the Labor
 * Category cost — docs/specs/resource-internal-rates.md). The header badge turns red
 * afterwards, but nothing says that THIS pick did it. This says so at the moment of the
 * pick. It never blocks: the edit has already saved, exactly as RP-18.7 requires.
 *
 * Pure: compares the plan before and after the edit, as the reader sees it. Margin % and
 * floor status already travel to every reader of the plan (non-negotiable 3), so the
 * message carries nothing the header does not.
 */

import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

export type MarginWarningPlan = Pick<
  ResourcePlanDetailView,
  "marginStatus" | "marginOfRecordPercent" | "marginFloorPercent" | "pendingApprovalId"
>;

/** The warning to show, or null when the plan did not newly drop below its floor. */
export function marginDropWarning(
  before: MarginWarningPlan | undefined,
  after: MarginWarningPlan,
): string | null {
  // Without the plan as it stood, a drop cannot be told apart from a plan already below.
  if (before === undefined) return null;
  if (before.marginStatus === "red" || after.marginStatus !== "red") return null;

  const drop =
    after.marginOfRecordPercent === null
      ? `The plan is now below the ${after.marginFloorPercent}% margin floor.`
      : `Margin is now ${after.marginOfRecordPercent}%, below the ${after.marginFloorPercent}% floor.`;
  const approval =
    after.pendingApprovalId !== null && before.pendingApprovalId === null
      ? " It has gone to Finance or an Admin for approval."
      : "";
  return `${drop}${approval}`;
}
